import { useState, useEffect, useCallback, useRef } from "react";
import { Opportunity } from "../api/types";
import { fetchMyOpportunities } from "../api/fetchMyOpportunities";
import { fetchOpportunitiesBySalesperson } from "../api/fetchOpportunitiesBySalesperson";
import { fetchOpportunitiesByProspect } from "../api/fetchOpportunitiesByProspect";

type SearchMode = "prospect" | "salesperson";

/**
 * Groups unlinked events (null opportunityId) into a single synthetic
 * "No Opportunity Linked" entry. Linked opportunities pass through as-is.
 */
function groupUnlinkedEvents(data: Opportunity[]): Opportunity[] {
  const linked: Opportunity[] = [];
  const unlinkedEvents: Opportunity["events"] = [];

  for (const item of data) {
    if (item.opportunityId !== null) {
      linked.push(item);
    } else {
      unlinkedEvents.push(...item.events);
    }
  }

  if (unlinkedEvents.length > 0) {
    linked.push({
      opportunityId: null,
      externalId: null,
      token: null,
      name: null,
      stageName: null,
      company: null,
      firstName: null,
      lastName: null,
      emailAddress: null,
      phoneNumber: null,
      addressLine1: null,
      addressLine2: null,
      cityName: null,
      postalCode: null,
      stateCode: null,
      stateName: null,
      countryCode: null,
      countryName: null,
      currency: null,
      timeZone: null,
      notes: null,
      trades: null,
      sentAt: null,
      openedAt: null,
      closedAt: null,
      paidAt: null,
      importDate: null,
      launchDate: null,
      invoiceDate: null,
      createdAt: null,
      updatedAt: null,
      account: null,
      agreement: null,
      events: unlinkedEvents,
    });
  }

  return linked;
}

export function useSalesPortalOpportunities() {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelPendingRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const createSignal = useCallback(() => {
    cancelPendingRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return controller.signal;
  }, [cancelPendingRequest]);

  const loadMyOpportunities = useCallback(async () => {
    const signal = createSignal();
    setLoading(true);
    fetchMyOpportunities(signal)
      .then((response) => {
        setOpportunities(groupUnlinkedEvents(response.data));
        setSelectedIndex(0);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [createSignal]);

  const searchOpportunities = useCallback(
    async (email: string, mode: SearchMode) => {
      const signal = createSignal();
      setLoading(true);
      const fetchFn =
        mode === "salesperson"
          ? fetchOpportunitiesBySalesperson(email, signal)
          : fetchOpportunitiesByProspect(email, signal);
      fetchFn
        .then((response) => {
          setOpportunities(groupUnlinkedEvents(response.data));
          setSelectedIndex(0);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [createSignal],
  );

  const clearOpportunities = useCallback(() => {
    cancelPendingRequest();
    setOpportunities([]);
    setSelectedIndex(0);
  }, [cancelPendingRequest]);

  useEffect(() => {
    return () => cancelPendingRequest();
  }, [cancelPendingRequest]);

  return {
    opportunities,
    selectedIndex,
    setSelectedIndex,
    loading,
    loadMyOpportunities,
    searchOpportunities,
    clearOpportunities,
  };
}
